import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./lib/language";
import { LeadModalProvider } from "./components/LeadModalContext";
import { LeadModal } from "./components/LeadModal";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { MobileCTABar, WhatsAppFloat } from "./components/MobileCTA";
import { track } from "./lib/track";

import Home from "./pages/Home";
import WhyDwc from "./pages/WhyDwc";
import Psychology from "./pages/Psychology";
import Programs from "./pages/Programs";
import ProgramDetail from "./pages/ProgramDetail";
import Audience from "./pages/Audience";
import Stories from "./pages/Stories";
import Guarantee from "./pages/Guarantee";
import Rto from "./pages/Rto";
import Franchise from "./pages/Franchise";
import Contact from "./pages/Contact";
import { Blog, BlogPost } from "./pages/Blog";
import Services, { LearningLicence, ManualPractice } from "./pages/Services";
import NotFound from "./pages/NotFound";
import DrivingProblems from "./pages/DrivingProblems";
import { applySeo } from "./lib/seo";
import { CartProvider } from "./store/CartContext";
import StoreHome from "./pages/store/StoreHome";
import StoreCatalog from "./pages/store/StoreCatalog";
import StoreProduct from "./pages/store/StoreProduct";
import StoreCart, { StoreCheckout } from "./pages/store/StoreCartPage";
import StoreOrderConfirmed, { StorePaymentSuccess, StoreTrack } from "./pages/store/StoreOrder";
import StorePolicies from "./pages/store/StorePolicies";
import LicencePrep from "./pages/LicencePrep";
import StoreAdmin from "./pages/store/StoreAdmin";
import PortalApp from "./pages/portal/PortalApp";
import { FloatingL } from "./components/Floating";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    applySeo(pathname);
    track("page_view");
  }, [pathname]);
  return null;
};

const Layout = () => (
  <div className="min-h-screen bg-white pb-[72px] md:pb-0">
    <ScrollManager />
    <Header />
    <main id="main">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/why-dwc" element={<WhyDwc />} />
        <Route path="/driving-psychology" element={<Psychology />} />
        <Route path="/driving-problems" element={<DrivingProblems />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/programs/:slug" element={<ProgramDetail />} />
        <Route path="/women-driving-training" element={<Audience kind="women" />} />
        <Route path="/senior-citizen-driving" element={<Audience kind="senior" />} />
        <Route path="/advanced-driving-training" element={<Audience kind="advanced" />} />
        <Route path="/success-stories" element={<Stories />} />
        <Route path="/students" element={<Stories />} />
        <Route path="/guarantee" element={<Guarantee />} />
        <Route path="/services" element={<Services />} />
        <Route path="/rto-services" element={<Rto />} />
        <Route path="/rto-consulting" element={<Rto />} />
        <Route path="/learning-licence" element={<LearningLicence />} />
        <Route path="/learning-licence-prep" element={<LicencePrep />} />
        <Route path="/manual-driving-practice" element={<ManualPractice />} />
        <Route path="/franchise" element={<Franchise />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/store" element={<StoreHome />} />
        <Route path="/store/shop" element={<StoreCatalog />} />
        <Route path="/store/product/:slug" element={<StoreProduct />} />
        <Route path="/store/cart" element={<StoreCart />} />
        <Route path="/store/checkout" element={<StoreCheckout />} />
        <Route path="/store/order-confirmed" element={<StoreOrderConfirmed />} />
        <Route path="/store/payment/success" element={<StorePaymentSuccess />} />
        <Route path="/store/track" element={<StoreTrack />} />
        <Route path="/store/policies" element={<StorePolicies />} />
        <Route path="/store/admin" element={<StoreAdmin />} />
        <Route path="/portal" element={<PortalApp />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
    <Footer />
    <MobileCTABar />
    <WhatsAppFloat />
    <FloatingL />
    <LeadModal />
    <Toaster position="top-center" richColors />
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09, smoothWheel: true });
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    const onContextMenu = (e) => {
      if (window.location.pathname.startsWith("/store/admin")) return;
      if (e.target.closest && e.target.closest("input, textarea, [contenteditable='true']")) return;
      e.preventDefault();
    };
    const onDragStart = (e) => {
      const tag = e.target.tagName;
      if (tag === "IMG" || tag === "VIDEO") e.preventDefault();
    };
    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("dragstart", onDragStart);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("dragstart", onDragStart);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <LeadModalProvider>
            <CartProvider>
              <Layout />
            </CartProvider>
          </LeadModalProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
